import dynamic from "next/dynamic";
import Link from "next/link";
import { gql } from "@apollo/client";
import useInView from "react-cool-inview";

import Image from "@/components/Image";
import Socials from "@/components/home/Socials";
import YouTube from "@/components/home/YouTube";
import EventsSection from "@/components/home/EventsSection";
import Seo from "@/components/seo";
import AdBar from "@/components/Ads/AdBar";
import HeroBackground, { useSpotlight } from "@/components/home/HeroBackground";
import { fetchYouTube } from "./api/youtube";

import styles from "@/styles/Home.module.css";
import handleStaticProps from "src/lib/handle-static-props";

const AlbumReviewGrid = dynamic(() =>
  import("@/components/common/AlbumReviewGrid")
);
const BlogPost = dynamic(() => import("@/components/home/BlogPost"));
const Topless39List = dynamic(() => import("@/components/home/Topless39List"));

export async function getStaticProps() {
  // Imported here rather than at module scope so the API key stays out of the
  // client bundle — Next only strips server-only code reached from this
  // function. Same reason as the events page.
  const { fetchCalendarEvents } = await import("src/lib/google-calendar");

  // Fetch YouTube
  const youtube = fetchYouTube();

  const { data } = await handleStaticProps({
    pageQuery,
  });

  // The calendar is one section of the home page, so it never takes the page
  // down with it — an unreachable Google means no events section, not a 500.
  let events = [];
  try {
    events = await fetchCalendarEvents();
  } catch (error) {
    console.error("Failed to fetch events for the home page:", error);
  }

  return {
    props: {
      data,
      youtube: await youtube,
      events,
      generatedAt: new Date().toISOString(),
    },
    revalidate: 60,
  };
}

function Home({ data, youtube, events, generatedAt }) {
  const spotlight = useSpotlight();
  const { observe, inView } = useInView({
    onEnter: ({ unobserve }) => unobserve(), // only run once
  });
  const { blogPosts, reviews, topless39 } = {
    blogPosts: data.allBlogPosts.edges,
    reviews: data.allReviews.edges,
    topless39: data.topChart?.albums || [],
  };

  return (
    <div className={`container ${styles.container}`}>
      <Seo />
      <section
        className={styles.hero}
        aria-labelledby="home-hero-title"
        ref={spotlight.ref}
        onPointerMove={spotlight.onPointerMove}
        onPointerLeave={spotlight.onPointerLeave}
      >
        <HeroBackground banner={data.heroBanner} />
        <div className={styles.heroInner}>
          <div className={styles.heroText}>
            {/* The wordmark is the display type here — the h1 wraps it so the
                heading outline and the section's aria-labelledby still work,
                with the alt text carrying the name. */}
            <h1 id="home-hero-title" className={styles.heroLogo}>
              <Image
                src="/static/main/images/logo-yellow.png"
                alt="KVRX 91.7 FM Austin"
                width={865}
                height={503}
                layout="responsive"
                priority
              />
            </h1>
            <p className={styles.tagline}>
              None of the hits, <span>all of the time</span>
            </p>
            <p className={styles.kicker}>
              Student-run radio broadcasting underground music, live shows, and
              local discoveries around the clock.
            </p>
            <div className={styles.heroActions}>
              <Link href="/schedule/" className={styles.secondaryAction}>
                View schedule
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.mediaGrid} aria-label="KVRX channels">
        <div className={styles.socialPanel}>
          <Socials />
        </div>
        <YouTube {...youtube} />
      </section>

      {/* Above the lazy block below: this one is short and time-sensitive, so
          it renders with the page rather than on scroll. */}
      <EventsSection events={events} generatedAt={generatedAt} />

      <AdBar />
      <div ref={observe}>
        {inView && (
          <>
            <section className={styles.contentSection}>
              <div className={styles.sectionHeader}>
                <p className={styles.eyebrow}>Blog</p>
                <h2>Recent features</h2>
              </div>
              <div className={styles.sectionBody}>
                <div className="row justify-content-center">
                  {blogPosts.map((post) => (
                    <BlogPost post={post} key={`blogpost-${post.id}`} />
                  ))}
                </div>
                <div className="text-end">
                  <Link href="/blog/" className={styles.link}>
                    MORE POSTS &gt;
                  </Link>
                </div>
              </div>
            </section>
            <section className={styles.contentSection}>
              <div className={styles.sectionHeader}>
                <p className={styles.eyebrow}>Reviews</p>
                <h2>New reviews</h2>
              </div>
              <div className={styles.sectionBody}>
                <AlbumReviewGrid reviews={reviews} />
                <div className="text-end">
                  <Link href="/reviews/" className={styles.link}>
                    MORE REVIEWS &gt;
                  </Link>
                </div>
              </div>
            </section>
            <section className={styles.contentSection}>
              <div className={styles.sectionHeader}>
                <p className={styles.eyebrow}>Charts</p>
                <h2>Topless 39</h2>
              </div>
              <div className={styles.sectionBody}>
                <Topless39List topless39={topless39} />
                <div className="text-end">
                  <Link href="/topless39/" className={styles.link}>
                    <span className="d-lg-inline">MORE TOPLESS 39 &gt;</span>
                  </Link>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default Home;

const pageQuery = gql`
  query HomePage {
    allBlogPosts(first: 6) {
      edges: objects {
        __typename
        id
        title
        date
        slug
        preview
        image {
          url
          width
          height
        }
        url
      }
    }
    heroBanner {
      url
      updatedAt
    }
    allReviews(first: 4) {
      edges: objects {
        __typename
        id
        cover
        artist
        title
        url
      }
    }
    topChart {
      albums(first: 5) {
        __typename
        id
        cover
        artist
        title
        url
      }
    }
  }
`;
